import {
    IImportHrFile,
    IObjectData,
    IResponse,
    IRouteDefinition,
    IRouteDefinitionParams,
    IRule,
    IRuleset,
    ISearchObjectParams,
    ITableColumn,
    TSyncfield,
} from "../../../index";
import {viewModel as SearchBasic} from "../../object/search.basic";
import {_} from 'lodash';
import * as ko from "knockout";
import {global, lm} from "../../../appContext";
import {createErrorMessage, getObjectDefinition, getObjectType, getSyncField} from "../../../util/misc";
import {getHrFileDate} from "../../../util/datetime";
import {getHash, getRouteDefinition} from "../../../constants/RouteDefinitions";
import {PLEASE_WAIT_MESSAGE} from "../../../constants/Language";
import PromiseRequest from "../../../req/PromiseRequest";
import {IDiffParams, TDiffKey, TIds} from "./index";
import {nsRuleset} from "../nsRuleset";
import {MAX_RESULTS_PER_PAGE, ObjectTypeId} from "../../../constants";
import {nsSyncFields} from "../../../req/nsSyncFields";
import {IComboboxV2Options} from "../../combobox/v2";
import {createServiceListPromise} from "../../combobox/v2-sources";
import {getSearchParamsFn} from "../../../util/request";
import {nsTooltip} from "../../../util/nsTooltip";
import getSyncfieldIdsFromNames = nsSyncFields.getSyncfieldIdsFromNames;
import previewHtml from './preview.html?raw';

export let template: string = previewHtml;

interface IParams extends IRouteDefinitionParams {
    ruleset: IRuleset
    rulesetOld?: IRuleset
}

export abstract class Preview extends SearchBasic {
    // whether we're comparing an edited against an original ruleset
    isCompare: boolean;

    ruleFields: string[];

    // mutable ruleset that is passed to the diff component - if it is updated we'll trigger a new preview
    importFile: KnockoutObservable<IImportHrFile | null> = ko.observable(null);

    // pass both rulesets (when editing) to the diff component to build new diff rulesets
    rulesetNew: IRuleset;
    rulesetOld: IRuleset | null;

    table!: ITableColumn[];
    showRuleFields: KnockoutObservable<boolean> = ko.observable(false);
    useDeleted!: boolean;
    private readonly searchRouteDef: IRouteDefinition | null;
    private pv = global.permissionValidator;
    private requests: PromiseRequest[] = [];

    private diffKey: KnockoutObservable<TDiffKey | null> = ko.observable(null);

    private ids: TIds = {
        newfull: null,
        newonly: null,
        intersect: null,
        oldonly: null,
        oldfull: null,
    }

    private diffParams: IDiffParams = {
        ids: this.ids,
        diffKey: this.diffKey,
    }

    private req_getsyncfieldidsfromnames!: Promise<string>
    private syncfieldIds!: string;

    constructor(params: IParams) {
        super(params, false);
        this.objecttypeid = params.ruleset.objecttype.id
        this.rulesetNew = params.ruleset;
        this.rulesetOld = params.rulesetOld || null;
        this.isCompare = !!this.rulesetOld;
        this.ruleFields = _.uniq(params.ruleset.rules.map((r: IRule) => r.attribute)).filter(Boolean);
        this.kosub.subscribe(this.importFile, () => this.loadIds());
        this.kosub.subscribe(this.diffKey, () => {
            this.data([])
            this.preRequest()
        });
        const objectDef = getObjectDefinition(this.rulesetNew.objecttype);
        this.searchRouteDef = objectDef?.routeSearch
            ? getRouteDefinition(objectDef.routeSearch, _.defaultsDeep({level: 1}, this.__routeDef))
            : null;
        this.loadIds()
    }
    private loadIds() {
        this.isReady(false)
        this.isRequesting(true)
        this.messageData({message: PLEASE_WAIT_MESSAGE})

        this.requests.forEach(req => req.abort());
        this.requests = [];

        Promise.resolve()
            .then(() => {
                // ids for NEW ruleset
                const req = global.getPromiseRequest("searchobject");
                this.requests.push(req);
                return req.request(_.defaultsDeep({onlyids: true}, this.getIdsParams(this.rulesetNew)))
                    .then((data: number[]) => {
                        this.ids.newfull = data
                    })
            })
            .then(() => {
                if (this.isCompare) {
                    return Promise.resolve()
                        .then(() => {
                            // ids for OLD ruleset
                            const req = global.getPromiseRequest("searchobject");
                            this.requests.push(req);
                            return req.request(_.defaultsDeep({onlyids: true}, this.getIdsParams(this.rulesetOld!)))
                                .then((data: number[]) => {
                                    this.ids.oldfull = data
                                })
                        })
                        .then(() => {
                            this.ids.newonly = _.difference(this.ids.newfull, this.ids.oldfull)
                            this.ids.intersect = _.intersection(this.ids.newfull, this.ids.oldfull)
                            this.ids.oldonly = _.difference(this.ids.oldfull, this.ids.newfull)
                        })
                }
            })
            .then(() => {
                this.isRequesting(false)
                this.messageData(null)
                this.isReady(true)
                if (!this.diffKey()) {
                    this.diffKey('newfull')
                    this.showAll(true)
                }
                this.preRequest()
            })
            .catch((reason: string) => {
                this.isRequesting(false)
                this.messageData(createErrorMessage(String(reason)))
            })
    }

    protected override request() {
        this.reqSearch.abort();
        this.isRequesting(true);
        if (!this.req_getsyncfieldidsfromnames) {
            const syncfieldNames: TSyncfield[] = [...this.table.map(o => String(o.key)), ...this.ruleFields] as TSyncfield[];
            if (this.objecttypeid == ObjectTypeId.User) {
                if (!syncfieldNames.includes('DisplayName') && syncfieldNames.includes('GivenName') && syncfieldNames.includes('Surname'))
                    syncfieldNames.push('DisplayName')
            }
            this.req_getsyncfieldidsfromnames = getSyncfieldIdsFromNames(this.objecttypeid, ...syncfieldNames);
        }
        this.req_getsyncfieldidsfromnames
            .then((ids: string) => this.syncfieldIds = ids)
            .then(() => this.reqSearch.request(this.getParams(), true))
            .then(this.onSuccess.bind(this))
            .then(() => this.isReady(true))
            .catch((resp: IResponse) => this.messageData(createErrorMessage(resp.lasterror)))
        ;
    }

    private getIdsParams(ruleset: IRuleset): ISearchObjectParams {
        const rs = _.cloneDeep(ruleset)

        // remove unnecessary attributes for the preview
        rs.description = '';
        rs.refs = {}

        return super.getParams({
            deleted: 0,
            importfileid: this.importFile()?.id ?? 0,
            jruleset: JSON.stringify(rs),
            objecttypeid: this.objecttypeid,
            resultsprpage: MAX_RESULTS_PER_PAGE,
        });
    }

    protected override getParams(params: Partial<ISearchObjectParams> = {}): ISearchObjectParams {
        const ids = this.ids[this.diffKey()!] ?? []
        const dataSourceOld = this.rulesetOld?.authdatasources
        const dataSourceNew = this.rulesetNew?.authdatasources
        const authdatasources = _([dataSourceOld, dataSourceNew]).filter(Boolean).uniq().join(nsRuleset.AUTH_DATA_SOURCES_DELIMITER)
        return super.getParams({
            deleted: 0,
            importfileid: this.importFile()?.id ?? 0,
            objecttypeid: this.objecttypeid,
            jruleset: JSON.stringify(nsRuleset.generateRuleset({
                authdatasources: authdatasources,
                objecttype: getObjectType(this.objecttypeid),
                rules: [nsRuleset.generateRule({
                    argument: ids.join(','),
                    argumentoperator: 'Er lik en av',
                    attribute: 'Id',
                })]
            })),
            syncfieldids: this.syncfieldIds,
        })
    }

    realTable = ko.pureComputed((): ITableColumn[] => {
        if (this.showRuleFields()) {
            return this.table.concat(this.ruleFields.map((attr: string) => {
                return {
                    label: attr,
                    key: attr,
                    sort: true
                };
            }));
        } else {
            return this.table;
        }
    });

    getItemLink(item: IObjectData): string {
        switch (this.objecttypeid) {
            case ObjectTypeId.User:
                return getHash('userdetails', {id: item.id}) ?? '';
            case ObjectTypeId.Group:
                return getHash('groupdetails', {id: item.id}) ?? '';
            case ObjectTypeId.Department:
                return getHash('departmentdetails', {id: item.id}) ?? '';
            default:
                return '';
        }
    }

    getSyncFieldTitle(key: TSyncfield, data: IObjectData): string {
        const syncField = getSyncField(key, data.syncfields);
        return (syncField && syncField.value) || `&mdash;`;
    }

    private getFilterOptionHrFileText(obj: IImportHrFile): string {
        return `${getHrFileDate(obj)}  (${String(obj.id)})`;
    }

    private getComboboxParams_synccycle(): IComboboxV2Options<IImportHrFile> {
        const listSource = createServiceListPromise<IImportHrFile>({
            name: 'searchimporthrfile',
            params: getSearchParamsFn('searchimporthrfile')({
                asc: false,
                resultsprpage: 20,
                sortcol: 'id',
            }),
        })
        return {
            promise: listSource.promise,
            onDispose: listSource.onDispose,
            prepareData: {
                filter: (o: IImportHrFile) => !!o.started && !!o.ended,
                sort: (arr: IImportHrFile[]) => _.sortBy(arr, (o: IImportHrFile) => o.id),
                reverse: true,
            },
            inputAttrs: {
                placeholder: lm.t('synchronization cycle'),
            },
            option: {
                sortBy: [Boolean],
                label: (o: IImportHrFile | null) => o?.started ?? '',
                tooltip: (o: IImportHrFile | null) => {
                    if (!o) {
                        return '';
                    }
                    const key = nsTooltip.getTitle_importHrFile(o);
                    return key ? (lm.t(key) ?? '') : '';
                },
            },
            selected: this.importFile,
        }
    }

}
